import { useMemo } from 'react';
import {
  validateCampaignOrderForRequestor,
  validateCampaignOrderForQueue,
} from '../utils/campaignOrderDataValidator';

/**
 * useCampaignOrderData
 *
 * Wraps a raw CampaignOrder GraphQL object and provides data-readiness gating.
 * This prevents downstream components from crashing on partially-populated
 * cache data or malformed backend responses.
 *
 * @param {object} campaignOrder – raw CampaignOrder from Apollo / context
 * @param {object} options
 * @param {'requestor'|'queue'} options.mode – which validation rules to apply
 * @param {boolean} options.requireCampaigns – require campaigns array (default: true)
 *
 * @returns {object}
 *   isReady         – boolean, true when data passes all validation checks
 *   missingFields   – string[] of failed checks (for debugging / telemetry)
 *   campaigns       – filtered campaigns array with null elements removed
 *   order           – the original campaignOrder (passthrough)
 */
export const useCampaignOrderData = (campaignOrder, options = {}) => {
  const { mode = 'requestor', requireCampaigns = true } = options;

  const result = useMemo(() => {
    if (!campaignOrder) {
      return {
        isReady: false,
        missingFields: ['campaignOrder is null'],
        campaigns: [],
        order: null,
      };
    }

    const validation =
      mode === 'queue'
        ? validateCampaignOrderForQueue(campaignOrder)
        : validateCampaignOrderForRequestor(campaignOrder, { requireCampaigns });

    return {
      isReady: validation.isReady,
      missingFields: validation.missingFields,
      campaigns: validation.campaigns || [],
      order: campaignOrder,
    };
  }, [campaignOrder, mode, requireCampaigns]);

  return result;
};
